import type { OrganizationSummary } from '@school-erp/types';
import { updateOrganizationSchema } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { TextField } from '../../components/TextField';
import { TimezoneCurrencyLists } from '../../components/Lists';
import { t } from '../../i18n';

export function OrganizationPage() {
  const { user, can } = useAuth();
  const queryClient = useQueryClient();
  const orgs = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api<OrganizationSummary[]>('/organizations'),
  });
  const org = orgs.data?.find((o) => o.id === user?.organizationId) ?? orgs.data?.[0];

  const [form, setForm] = useState({ name: '', timezone: '', currencyCode: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (org) setForm({ name: org.name, timezone: org.timezone, currencyCode: org.currencyCode });
  }, [org]);

  const save = useMutation({
    mutationFn: (body: object) => api(`/organizations/${org!.id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      setMessage({ kind: 'ok', text: t('common.saved') });
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
    onError: (error) =>
      setMessage({
        kind: 'error',
        text: error instanceof ApiError ? error.message : t('common.actionError'),
      }),
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setMessage(null);
    const parsed = updateOrganizationSchema.safeParse(form);
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])));
      return;
    }
    setErrors({});
    save.mutate(parsed.data);
  }

  if (orgs.isPending) return <p role="status">{t('common.loading')}</p>;
  if (orgs.isError || !org) return <p role="alert">{t('common.loadError')}</p>;
  const editable = can('orgs.update');

  return (
    <div className="mx-auto max-w-xl space-y-4">
      <h1 className="text-2xl font-bold">{t('org.title')}</h1>
      <p className="text-sm text-slate-600">
        {t('org.slug')}: <code>{org.slug}</code>
      </p>
      <TimezoneCurrencyLists />
      <form
        onSubmit={onSubmit}
        noValidate
        className="space-y-4 rounded-lg border border-slate-200 bg-white p-4"
      >
        <TextField
          label={t('org.name')}
          value={form.name}
          disabled={!editable}
          error={errors.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <TextField
          label={t('org.timezone')}
          list="timezone-options"
          value={form.timezone}
          disabled={!editable}
          error={errors.timezone}
          onChange={(e) => setForm({ ...form, timezone: e.target.value })}
        />
        <TextField
          label={t('org.currency')}
          list="currency-options"
          value={form.currencyCode}
          disabled={!editable}
          error={errors.currencyCode}
          onChange={(e) => setForm({ ...form, currencyCode: e.target.value })}
        />
        {message && (
          <p
            role={message.kind === 'error' ? 'alert' : 'status'}
            className={message.kind === 'error' ? 'text-sm text-red-700' : 'text-sm text-green-800'}
          >
            {message.text}
          </p>
        )}
        {editable && (
          <button
            type="submit"
            disabled={save.isPending}
            className="rounded bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-900 disabled:opacity-50"
          >
            {save.isPending ? t('common.saving') : t('common.save')}
          </button>
        )}
      </form>
    </div>
  );
}
